/**
 * 修正: レシピ子カテゴリページ — 統合記事一覧
 *
 * Shopify Liquid 単体では
 * 「子カテゴリ記事 + 複数孫カテゴリ記事 + 重複除外」を
 * 1つの paginate 配列として扱えないため、一覧の統合は JS 側で行う。
 */
type RecipeChildSource = {
  url: string;
  total: number;
};

type RecipeChildConfig = {
  perPage: number;
  sources: RecipeChildSource[];
};

type ArticleItem = {
  key: string;
  html: string;
};

const PAGE_PARAM_PATTERN = /^page(?:_.+)?$/;

const normalizeArticleKey = (value: string | null) => {
  if (!value) return "";

  try {
    const url = new URL(value, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
};

const getPersistentSearchParams = () => {
  const params = new URLSearchParams(window.location.search);

  for (const key of [...params.keys()]) {
    if (PAGE_PARAM_PATTERN.test(key)) {
      params.delete(key);
    }
  }

  return params;
};

const getCurrentPage = () => {
  const current = Number(new URLSearchParams(window.location.search).get("page"));
  return Number.isFinite(current) && current > 0 ? current : 1;
};

const buildPageHref = (page: number) => {
  const params = getPersistentSearchParams();
  params.set("page", String(page));

  const query = params.toString();
  return query ? `${window.location.pathname}?${query}` : window.location.pathname;
};

const buildSourceUrl = (
  sourceUrl: string,
  pageParam: string | null = null,
  page = 1,
) => {
  const url = new URL(sourceUrl, window.location.origin);
  const params = getPersistentSearchParams();

  if (pageParam && page > 1) {
    params.set(pageParam, String(page));
  }

  url.search = params.toString();
  return url.toString();
};

const fetchDocument = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }

  const html = await response.text();
  return new DOMParser().parseFromString(html, "text/html");
};

const detectPageParam = (doc: Document) => {
  const links = doc.querySelectorAll<HTMLAnchorElement>("a[href]");

  for (const link of links) {
    const href = link.getAttribute("href") || "";
    const match = href.match(/[?&](page_[^=]+)=\d+/);
    if (match) {
      return match[1];
    }
  }

  return "page";
};

const extractArticleItems = (doc: Document) => {
  const items = doc.querySelectorAll<HTMLElement>(".p-blog-recipe__article-item");

  return [...items]
    .map((item) => {
      const fallbackHref = item.querySelector<HTMLAnchorElement>("a[href]");
      const key =
        normalizeArticleKey(item.dataset.articleUrl || fallbackHref?.href || null) ||
        item.outerHTML;

      return {
        key,
        html: item.outerHTML,
      };
    })
    .filter((item) => item.key !== "");
};

const collectSourceItems = async (
  source: RecipeChildSource,
  perPage: number,
) => {
  const totalPages = Math.max(1, Math.ceil(source.total / perPage));
  const firstDoc = await fetchDocument(buildSourceUrl(source.url));
  const pageParam = detectPageParam(firstDoc);
  const pageResults = [{ page: 1, items: extractArticleItems(firstDoc) }];

  if (totalPages > 1) {
    const responses = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2).map(
        async (page) => {
          const doc = await fetchDocument(buildSourceUrl(source.url, pageParam, page));
          return {
            page,
            items: extractArticleItems(doc),
          };
        },
      ),
    );

    pageResults.push(...responses);
  }

  return pageResults
    .sort((left, right) => left.page - right.page)
    .flatMap((result) => result.items);
};

const collectMergedItems = async (
  sources: RecipeChildSource[],
  perPage: number,
) => {
  const mergedItems: ArticleItem[] = [];
  const seenKeys = new Set<string>();

  for (const source of sources) {
    const sourceItems = await collectSourceItems(source, perPage);

    for (const item of sourceItems) {
      if (seenKeys.has(item.key)) {
        continue;
      }

      seenKeys.add(item.key);
      mergedItems.push(item);
    }
  }

  return mergedItems;
};

const ensurePaginationRoot = (section: HTMLElement, grid: HTMLElement) => {
  const existing = section.querySelector<HTMLElement>("[data-recipe-pagination]");
  if (existing) return existing;

  const root = document.createElement("div");
  root.className = "pagination-wrapper";
  root.dataset.recipePagination = "";
  grid.insertAdjacentElement("afterend", root);
  return root;
};

const renderPagination = (
  root: HTMLElement,
  currentPage: number,
  totalPages: number,
) => {
  if (totalPages <= 1) {
    root.hidden = true;
    root.innerHTML = "";
    return;
  }

  root.hidden = false;
  root.dataset.page = String(currentPage);

  const pagesMarkup = Array.from({ length: totalPages }, (_, index) => index + 1)
    .map((page) => {
      if (page === currentPage) {
        return `
          <li>
            <a
              role="link"
              aria-disabled="true"
              class="pagination__item pagination__item--current light"
              aria-current="page"
              aria-label="Page ${page}"
            >
              ${page}
            </a>
          </li>
        `;
      }

      return `
        <li>
          <a
            href="${buildPageHref(page)}"
            class="pagination__item link"
            aria-label="Page ${page}"
            data-pagination-page="${page}"
          >
            ${page}
          </a>
        </li>
      `;
    })
    .join("");

  const prevMarkup =
    currentPage > 1
      ? `
        <li>
          <a
            href="${buildPageHref(currentPage - 1)}"
            class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce"
            aria-label="Previous page"
            data-pagination-page="${currentPage - 1}"
          >
            <span class="svg-wrapper">
              <svg class="icon icon-caret" viewBox="0 0 10 6">
                <path
                  fill="currentColor"
                  fill-rule="evenodd"
                  d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 1 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </span>
          </a>
        </li>
      `
      : "";

  const nextMarkup =
    currentPage < totalPages
      ? `
        <li>
          <a
            href="${buildPageHref(currentPage + 1)}"
            class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce"
            aria-label="Next page"
            data-pagination-page="${currentPage + 1}"
          >
            <span class="svg-wrapper">
              <svg class="icon icon-caret" viewBox="0 0 10 6">
                <path
                  fill="currentColor"
                  fill-rule="evenodd"
                  d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 1 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </span>
          </a>
        </li>
      `
      : "";

  root.innerHTML = `
    <nav class="pagination" role="navigation" aria-label="Pagination">
      <ul class="pagination__list list-unstyled" role="list">
        ${prevMarkup}
        ${pagesMarkup}
        ${nextMarkup}
      </ul>
    </nav>
  `;
};

const parseConfig = (section: HTMLElement) => {
  const configElement = section.querySelector<HTMLScriptElement>(
    "[data-recipe-child-config]",
  );
  if (!configElement?.textContent) return null;

  try {
    const parsed = JSON.parse(configElement.textContent) as RecipeChildConfig;
    const sources = Array.isArray(parsed.sources)
      ? parsed.sources.filter(
          (source): source is RecipeChildSource =>
            typeof source?.url === "string" &&
            typeof source?.total === "number" &&
            source.total > 0,
        )
      : [];

    return {
      perPage:
        typeof parsed.perPage === "number" && parsed.perPage > 0
          ? parsed.perPage
          : 50,
      sources,
    };
  } catch (error) {
    console.error("[recipeChild] config parse error:", error);
    return null;
  }
};

export function recipeChild(): void {
  const section = document.querySelector<HTMLElement>(
    "[data-recipe-child-section]",
  );
  if (!section) return;

  const grid = section.querySelector<HTMLElement>("[data-recipe-articles-grid]");
  if (!grid) return;

  const config = parseConfig(section);
  if (!config || config.sources.length === 0) return;

  const paginationRoot = ensurePaginationRoot(section, grid);
  let mergedItems: ArticleItem[] = [];

  const render = (page: number, updateHistory = false) => {
    const totalPages = Math.max(1, Math.ceil(mergedItems.length / config.perPage));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const startIndex = (safePage - 1) * config.perPage;
    const pageItems = mergedItems.slice(startIndex, startIndex + config.perPage);

    // 修正: 統合済みの記事カードだけを子カテゴリページに再描画する
    grid.innerHTML = pageItems.map((item) => item.html).join("");
    renderPagination(paginationRoot, safePage, totalPages);

    if (updateHistory) {
      history.pushState(null, "", buildPageHref(safePage));
    } else if (safePage !== page) {
      history.replaceState(null, "", buildPageHref(safePage));
    }
  };

  // 修正: ページ番号だけを子カテゴリ URL に反映し、再 fetch なしで切り替える
  section.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest<HTMLAnchorElement>("[data-pagination-page]");
    if (!link) return;

    event.preventDefault();

    const nextPage = Number(link.dataset.paginationPage);
    if (!Number.isFinite(nextPage) || nextPage < 1) return;

    render(nextPage, true);
  });

  window.addEventListener("popstate", () => {
    if (mergedItems.length === 0) return;
    render(getCurrentPage());
  });

  void collectMergedItems(config.sources, config.perPage)
    .then((items) => {
      mergedItems = items;
      render(getCurrentPage());
    })
    .catch((error) => {
      console.error("[recipeChild] merge error:", error);
    });
}
